import { Injectable } from '@angular/core';
import {
    HistoricalTrends,
    IHeadcountTrendsData,
    IHistoricalDataParameters,
} from '../interfaces/historical-trends';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MilestoneRoleVolumeByPeriod } from '../models/MilestoneRoleVolumeByPeriod.model';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import * as Highcharts from 'highcharts';
import {
    DisplayOptions,
    IHistoricalYears,
    IScorecardDateRange,
} from 'app/modules/shared/interfaces/display-options';
import { Milestone } from 'app/modules/shared/interfaces/milestone';
import { HistoricalTrendsChartOptions } from 'app/modules/shared/classes/historical-trends-chart-options';
import { Router } from '@angular/router';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';
import { ChartdataSeries } from 'app/modules/pipeline/interfaces/pipeline-data';
import { StreamgraphChartOptions } from 'app/modules/shared/classes/historical-trends-streamgraph';
import { HeadcountTrendsChartOptions } from 'app/modules/shared/classes/headcount-trends-chart-options';
import { EmployeePositionService } from 'app/modules/settings/services/employee-position.service';
import { DisplayOptionsService } from 'app/modules/shared/services/display-options.service';

@Injectable({
    providedIn: 'root',
})
export class HistoricalTrendsService {
    yMaxUnits: number;
    yMaxVolume: number;
    constructor(
        private _http: HttpClient,
        private reusableFunctions: ReusableFunctions,
        private router: Router,
        private userAccessService: UserAccessService,
        private employeePositionServce: EmployeePositionService,
        private displayOptionsService: DisplayOptionsService
    ) {}

    private serverUrl = 'https://' + window.location.host;
    private version = '0.5';
    private componentName = 'historicaltrends';
    private _url = `${this.serverUrl}/api/v${this.version}/${this.componentName}`;

    getHistoricalTrends(
        years: string[],
        datePeriodUnit: string,
        milestoneKey: number,
        channel: string,
        countBy: string,
        loanPurpose: string,
        loanType: string,
        hFilterJSON: string
    ): Observable<HistoricalTrends[]> {
        let params = {
            datePeriodUnit: datePeriodUnit,
            milestoneKey: milestoneKey.toString(),
            channel: channel,
            countBy: countBy,
            loanPurpose: loanPurpose,
            loanType: loanType,
            hFilterJSON: hFilterJSON,
            years: years,
            displayAs: 'Units',
        };
        return this._http.post<HistoricalTrends[]>(this._url + '/GetHistoricalTrend', params, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }

    getMilestoneRoleVolumeByPeriod(
        roleName: string,
        viewBy: string,
        milestoneKey: number,
        hFilterJson: string
    ): Observable<MilestoneRoleVolumeByPeriod[]> {
        const params = {
            roleName: roleName,
            viewBy: viewBy,
            milestoneKey: milestoneKey.toString(),
            hFilterJson: hFilterJson,
        };
        return this._http.post<MilestoneRoleVolumeByPeriod[]>(
            this._url + '/GetMilestoneRoleVolumeByPeriod',
            params,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    geHeadcountData(parameters: IHistoricalDataParameters): Observable<IHeadcountTrendsData[]> {
        return this._http.post<IHeadcountTrendsData[]>(
            this._url + '/GetHeadcountTrendsData',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    initializeLargeCard(
        largeCardInfo: ICardInfo,
        action: NoDataActionType,
        cardDrilldownUrl: string,
        chartReset: boolean = false
    ) {
        let emptyChart = null;
        if (chartReset) {
            emptyChart = new Highcharts.Chart(this.getUniqueId(largeCardInfo.drillDUrlSegment), {});
        }

        let largeCardInteriorOptions = {
            componentTitle: largeCardInfo.name,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: false,
            accessToUrl: cardDrilldownUrl + '/' + largeCardInfo.urlSegment,
            uniqueId: largeCardInfo.cardId,
            chart: emptyChart,
            action,
        };
        largeCardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    getUniqueId(urlSegment: string) {
        const drilldownBaseUrl = '/reporting/historical-trends';
        let urlitems = (drilldownBaseUrl + urlSegment).split('/');
        let id1 = urlitems[urlitems.length - 2] ? urlitems[urlitems.length - 2] : 'id-first';
        let id2 = urlitems[urlitems.length - 1] ? urlitems[urlitems.length - 1] : 'id-second';
        drilldownBaseUrl + urlSegment;
        return (id1 + id2).toLowerCase();
    }

    setHistoricalTrendsAAGCardsData(
        displayOptions: DisplayOptions[],
        hFilterJson: string,
        cardInfo: ICardInfo,
        action: NoDataActionType,
        milestone: Milestone
    ) {
        const colorOptions = this.reusableFunctions.getColorOptions('historical');
        const yearOptions = JSON.parse(localStorage.getItem('historical-year-options'));
        let params = this.setHistoricalTrendsAAGParameters(
            displayOptions,
            hFilterJson,
            yearOptions,
            cardInfo
        );
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        largeCardInteriorOptions.hasData = false;
        largeCardInteriorOptions.isDataReady = false;

        const chartTitleData = {
            showChartCustomTitle: true,
            chartTile: cardInfo.name,
            chartMainValue: '',
            showMainValue: false,
            showDashboardTextRight: false,
        };
        largeCardInteriorOptions.chartTitleData = chartTitleData;

        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
        const uniqueChartId = cardInfo.cardId;

        this.getHistoricalTrends(
            params.years,
            params.viewBy,
            params.milestoneKey,
            params.channel,
            params.countBy,
            params.loanPurpose,
            params.loanType,
            hFilterJson
        ).subscribe(
            (res) => {
                this.constructHistoricalTrendsCharts(
                    res,
                    params.viewBy,
                    colorOptions,
                    milestone,
                    cardInfo,
                    uniqueChartId,
                    yearOptions
                );
            },
            (error) => {
                console.log('error: ', error);
            }
        );
    }

    setHistoricalTrendsAAGParameters(
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        yearOptions,
        cardInfo
    ) {
        let viewBy =
            displayOptions.find((x) => x.optionKey == 'viewBy_m_w')?.selectedOptions ?? 'Month';
        let countBy =
            displayOptions.find((x) => x.optionKey == 'countBy')?.selectedOptions ?? 'Total';
        let channel = displayOptions.find((x) => x.optionKey == 'channel').selectedOptions;
        let loanPurpose = displayOptions.find((x) => x.optionKey == 'loanPurpose').selectedOptions;
        let loanType = displayOptions.find((x) => x.optionKey == 'loanCategory').selectedOptions;
        const years = yearOptions.map((y) => y.year);

        return {
            years,
            viewBy,
            milestoneKey: cardInfo.milestoneKey,
            channel,
            countBy,
            loanPurpose,
            loanType,
            hFilterJSON,
        };
    }

    constructHistoricalTrendsCharts(
        data: HistoricalTrends[],
        viewBy: string,
        colors: object,
        milestoneValues: Milestone,
        chartInfo: any,
        uniqueChartId: string,
        yearOptions: IHistoricalYears[]
    ) {
        const drillDownBaseUrl = '/reporting/historical-trends';
        if (chartInfo) {
            let largeCardInteriorOptions = chartInfo.largeCardInteriorOptions;
            largeCardInteriorOptions.queryParams = {
                milestonekey: milestoneValues.milestoneKey,
                year: new Date().getFullYear(),
            };
            let yMax = 0;

            if (data) {
                if (chartInfo) {
                    chartInfo.milestoneInfo = milestoneValues;
                    let displayAs = 'Volume';
                    let isScallingByUnit = this.isScalingByUnits(data, 'Units', chartInfo);
                    if (isScallingByUnit) {
                        yMax = this.yMaxUnits;
                        displayAs = 'Units';
                    } else {
                        yMax = this.yMaxVolume;
                    }

                    let seriesByYear = [];
                    yearOptions.forEach((yearInf, index) => {
                        let dataPerYear = data.filter(
                            (item) => item.year.toString() === yearInf.year.toString()
                        );
                        let seriesData = dataPerYear.map((i) => {
                            return {
                                name:
                                    viewBy === 'Month'
                                        ? this.reusableFunctions.getMonthName(i.datePeriod)
                                              .shortMonth
                                        : i.datePeriod,
                                y: isScallingByUnit
                                    ? i.nbr
                                    : i.amount === null
                                    ? null
                                    : Math.round(i.amount),
                                properties: {
                                    datePeriodDescription: i.datePeriodDescription,
                                    year: i.year,
                                    units: i.nbr,
                                    volume: i.amount,
                                    datePeriod: i.datePeriod,
                                    amountPercentChange: i.amountPercentChange,
                                },
                            };
                        });

                        seriesByYear.push({
                            data: seriesData,
                            type: 'spline',
                            color: colors[yearInf.yearInfo.index],
                            marker: {
                                symbol: 'circle',
                            },
                            visible: yearInf.yearInfo.visible,
                            name: yearInf.year,
                        } as Highcharts.SeriesOptionsType);
                    });
                    let limeGearCharts = new HistoricalTrendsChartOptions(
                        this.reusableFunctions,
                        this.router
                    );

                    largeCardInteriorOptions.chart = new Highcharts.Chart(
                        uniqueChartId,
                        limeGearCharts.createNewHistoricalChartOptions(
                            chartInfo.name,
                            viewBy,
                            displayAs,
                            seriesByYear,
                            chartInfo.drillDUrlSegment,
                            drillDownBaseUrl,
                            chartInfo.milestoneInfo.milestoneKey,
                            this.checkAccess(drillDownBaseUrl + '/' + chartInfo.drillDUrlSegment),
                            yMax,
                            !chartInfo.showTitle
                        )
                    );
                    largeCardInteriorOptions.hasData = data.length > 0 ? true : false;
                }
            }

            largeCardInteriorOptions.showCardControlArea = true;
            largeCardInteriorOptions.isDataReady = true;
            chartInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
        }
    }

    isScalingByUnits(value: HistoricalTrends[], scaleBy: string, cardInfo) {
        if (!value || value.length < 1) {
            return false;
        }
        // let scaled = 'unitsScaled';
        // if (scaleBy.toLowerCase() === 'volume') {
        //     scaled = 'volumeScaled';
        // }

        // let milestones = this.reusableFunctions.getUniqueItemList(
        //     this.historicalTrendsCharts.filter((item) => item[scaled] === true),
        //     'databaseName'
        // );
        // let index = milestones.findIndex(
        //     (item) => item.toLowerCase() === value[0].milestone.toLowerCase()
        // );

        if (cardInfo.unitsScaled) {
            return true;
        }
        return false;
    }

    checkAccess(url: string) {
        return this.userAccessService.hasAccessToPage(url);
    }

    setSteamgraphData(
        displayOptions: DisplayOptions[],
        hFilterJson: string,
        cardInfo: ICardInfo,
        action: NoDataActionType
    ) {
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        largeCardInteriorOptions.hasData = false;
        largeCardInteriorOptions.isDataReady = false;
        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
        const uniqueChartId = cardInfo.cardId;

        this.employeePositionServce.getHierarchyLevels().subscribe((res) => {
            const params = this.setSteamgraphParameters(res, displayOptions, hFilterJson);
            this.getMilestoneRoleVolumeByPeriod(
                params.roleName,
                params.viewBy,
                parseInt(params.milestone),
                params.hFilterJSON
            ).subscribe(
                (res: MilestoneRoleVolumeByPeriod[]) => {
                    this.constructSteamgraph(res, cardInfo);
                },
                (err) => {
                    largeCardInteriorOptions.hasData = false;
                    largeCardInteriorOptions.isDataReady = true;
                    largeCardInteriorOptions.action = NoDataActionType.error;
                    cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
                }
            );
        }),
            (err) => {
                console.log('error: ', err);
            };
    }

    setSteamgraphParameters(res, displayOptions: DisplayOptions[], hFilterJSON: string) {
        const highestRole = res.filter((x) => x.roleOrder < 10 && x.isActive)[0].roleName;
        let viewBy =
            displayOptions.find((x) => x.optionKey == 'viewBy_m_q_y')?.selectedOptions ?? 'Month';
        let milestone =
            displayOptions.find((x) => x.optionKey == 'volumeBy')?.selectedOptions ?? '22';
        let roleName =
            displayOptions.find((x) => x.optionKey == 'groupByRolesDfaultSelAtFirst')
                ?.selectedOptions ?? highestRole;

        return {
            viewBy,
            milestone,
            roleName,
            hFilterJSON,
        };
    }

    constructSteamgraph(data: MilestoneRoleVolumeByPeriod[], cardInfo) {
        let allSeriesData: ChartdataSeries[] = [];
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;

        if (data) {
            let datePeriods = this.reusableFunctions.getUniqueItemList(
                data,
                'datePeriodDescription'
            );
            let hierarchyIds = this.reusableFunctions.getUniqueItemList(
                data,
                'hierarchyIdentifier'
            );
            interface StreamGrapgData {
                y: number;
                name: string;
                properties: object;
            }

            for (let hId of hierarchyIds) {
                let dataOfHierarchy = data.filter((h) => h.hierarchyIdentifier === hId);
                let seriesData: StreamGrapgData[] = [];
                datePeriods.forEach((period) => {
                    let hierarchy = dataOfHierarchy.find((h) => h.datePeriodDescription === period);
                    seriesData.push({
                        y: hierarchy ? hierarchy.amount : 0,
                        name: period,
                        properties: {
                            display: hierarchy ? '' : 'none',
                            hierarchyName: hierarchy ? hierarchy.hierarchyName : '',
                            units: hierarchy ? hierarchy.nbr : 0,
                        },
                    });
                });
                allSeriesData.push({
                    name: dataOfHierarchy[0].hierarchyName,
                    type: 'streamgraph',
                    data: seriesData,
                    marker: {
                        enabled: seriesData.length === 1 ? true : false,
                    },
                });
            }
            const titleData = {
                text: 'Volume Over Time',
                style: {
                    color: '#333333',
                    fontSize: '27px',
                },
                align: 'left',
                x: 22,
            };

            allSeriesData.sort((a, b) => this.reusableFunctions.sortAlphaNumeric(a.name, b.name));
            const streamGraphOptions = new StreamgraphChartOptions(this.reusableFunctions);
            largeCardInteriorOptions.showCardControlArea = true;
            largeCardInteriorOptions.hasData = allSeriesData.length > 0;
            largeCardInteriorOptions.isDataReady = true;
            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                streamGraphOptions.loadloadChartOptions(allSeriesData, titleData)
            );
            cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
        }
    }

    setHeadcountData(
        displayOptions: DisplayOptions[],
        hFilterJson: string,
        cardInfo: ICardInfo,
        action: NoDataActionType
    ) {
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        largeCardInteriorOptions.hasData = false;
        largeCardInteriorOptions.isDataReady = false;
        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
        const uniqueChartId = cardInfo.cardId;
        this.displayOptionsService
            .getScorecardRanges('')
            .subscribe((res: IScorecardDateRange[]) => {
                const params = this.setHeadcountParameters(res, displayOptions, hFilterJson);
                this.geHeadcountData(params).subscribe((res: IHeadcountTrendsData[]) => {
                    if (res?.length > 0) {
                        this.constructHeadcountTrendsChart(res, cardInfo);
                    } else {
                        largeCardInteriorOptions.hasData = false;
                        largeCardInteriorOptions.isDataReady = true;
                    }
                }),
                    (err) => {
                        largeCardInteriorOptions.hasData = false;
                        largeCardInteriorOptions.isDataReady = true;
                        largeCardInteriorOptions.action = NoDataActionType.error;
                        cardInfo.largeCardInteriorOptions = Object.assign(
                            {},
                            largeCardInteriorOptions
                        );
                    };
            }),
            (err) => {
                console.log('err: ', err);
            };
    }

    setStreamgraphParameters(displayOptions: DisplayOptions[], hFilterJSON: string) {}

    setHeadcountParameters(
        res: IScorecardDateRange[],
        displayOptions: DisplayOptions[],
        hFilterJSON: string
    ) {
        const defaultedAsOfDate = res[0].optionIdentifier;
        let fromDatePeriod =
            displayOptions.find((x) => x.optionKey == 'dateRangeRolling_to_36m')?.selectedOptions ??
            'This Year';
        let asOf =
            displayOptions.find((x) => x.optionKey == 'scorecardAsOf')?.selectedOptions ??
            defaultedAsOfDate;
        let positionAbbv =
            displayOptions.find((x) => x.optionKey == 'groupByPosition_multiSelect')
                ?.selectedOptions ?? 'All';
        return {
            fromDatePeriod,
            asOf,
            positionAbbv,
            hFilterJSON,
        };
    }

    constructHeadcountTrendsChart(data, cardInfo) {
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;

        let positions = this.reusableFunctions.getUniqueItemList(data, 'position');
        let allSeriesData = [];

        positions.forEach((pos) => {
            let seriesData = {
                name: pos,
                type: 'spline',
                data: [],
            };

            let positionDataSet = data.filter((item) => item.position === pos);
            positionDataSet.forEach((point) => {
                let pointData = {
                    y: point.employeeCount,
                    name: point.yearMonthCalendar,
                    properties: {
                        positionAbbv: point.positionAbbv,
                        position: point.position,
                    },
                };

                seriesData.data.push(pointData);
            });
            allSeriesData.push(seriesData);
        });
        const headcountTrendsChartOptions = new HeadcountTrendsChartOptions(
            this.reusableFunctions,
            this.router
        );
        const titleData = {
            text: 'Headcount Trends',
            style: {
                color: '#333333',
                fontSize: '27px',
            },
            align: 'left',
            x: 22,
        };

        let chartOptions = headcountTrendsChartOptions.createNewHistoricalChartOptions(
            null,
            'Headcount',
            allSeriesData,
            '/reporting/historical-trends/insights_headcount-trends',
            true,
            titleData,
            true
        );

        largeCardInteriorOptions.hasData = data.length > 0;
        largeCardInteriorOptions.isDataReady = true;
        largeCardInteriorOptions.chart = new Highcharts.Chart(
            largeCardInteriorOptions.uniqueId,
            chartOptions
        );
        largeCardInteriorOptions.showCardControlArea = true;

        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }
}
